import React, { useState } from 'react';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col,
  Spinner, Alert, InputGroup, Badge,
} from 'react-bootstrap';
import {
  PlusIcon, PencilSquareIcon, TrashIcon,
  MagnifyingGlassIcon, XMarkIcon, BookOpenIcon,
  BeakerIcon, FireIcon, EyeIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCompanyRecipes, RECIPE_TYPE_OPTIONS } from './hooks/useCompanyRecipes';

const thStyle: React.CSSProperties = {
  color: '#475569', fontWeight: 600, fontSize: '0.8rem',
  textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px',
};

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  bread:   { bg: '#fef9c3', color: '#854d0e' },
  pastry:  { bg: '#fce7f3', color: '#9d174d' },
  cake:    { bg: '#ede9fe', color: '#5b21b6' },
  cookie:  { bg: '#fee2e2', color: '#991b1b' },
  pizza:   { bg: '#ffedd5', color: '#9a3412' },
  cracker: { bg: '#f1f5f9', color: '#475569' },
  other:   { bg: '#f1f5f9', color: '#475569' },
};

const CompanyRecipes: React.FC = () => {
  const {
    isExpired, loading, search, setSearch, typeFilter, setTypeFilter,
    filtered, recipes, allIngredients, allStarters,
    showModal, editingRecipe, form, setForm, lines, saving,
    feedback, setFeedback, deleteState,
    handleOpenCreate, handleOpenEdit, addIngredientLine, addStarterLine,
    removeLine, updateLine, handleSave, handleConfirmDelete, handleDelete,
    closeModal, closeDeleteModal,
  } = useCompanyRecipes();

  const [viewingRecipe, setViewingRecipe] = useState<any | null>(null);

  if (loading) {
    return (
      <CompanyLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 text-muted">Loading recipes...</p>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {feedback.success && (
          <Alert variant="success" dismissible onClose={() => setFeedback(p => ({ ...p, success: '' }))} className="mb-3">
            {feedback.success}
          </Alert>
        )}
        {feedback.error && !showModal && (
          <Alert variant="danger" dismissible onClose={() => setFeedback(p => ({ ...p, error: '' }))} className="mb-3">
            {feedback.error}
          </Alert>
        )}

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{ backgroundColor: '#ecfdf5', borderRadius: '8px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BookOpenIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Recipes</h4>
            <Badge bg="" style={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.75rem', fontWeight: '600', padding: '4px 10px', borderRadius: '6px' }}>
              {recipes.length}
            </Badge>
          </div>
          <Button variant="success" size="sm" className="d-flex align-items-center gap-1"
            onClick={handleOpenCreate} disabled={isExpired} style={{ padding: '6px 14px' }}>
            <PlusIcon style={{ width: '15px', height: '15px' }} />
            New Recipe
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
          <Card.Body className="py-3">
            <Row className="g-2 align-items-center">
              <Col xs={12} md={6}>
                <InputGroup>
                  <InputGroup.Text style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px 0 0 6px' }}>
                    <MagnifyingGlassIcon style={{ width: '15px', height: '15px', color: '#94a3b8' }} />
                  </InputGroup.Text>
                  <Form.Control placeholder="Search recipes..." value={search} onChange={e => setSearch(e.target.value)}
                    style={{ border: '1px solid #e2e8f0', borderRadius: '0 6px 6px 0' }} />
                  {search && (
                    <Button variant="outline-secondary" size="sm" onClick={() => setSearch('')} style={{ borderRadius: '6px' }}>
                      <XMarkIcon style={{ width: '14px', height: '14px' }} />
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col xs={12} md={3}>
                <Form.Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                  <option value="all">All Types</option>
                  {RECIPE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Col>
              <Col className="text-muted" style={{ fontSize: '0.85rem' }}>
                {filtered.length} of {recipes.length} recipes
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={thStyle}>Recipe</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Type</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Yield</th>
                  <th style={thStyle} className="d-none d-lg-table-cell">Ingredients</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      <BookOpenIcon style={{ width: '32px', height: '32px', opacity: 0.3, marginBottom: '8px' }} />
                      <div>{search || typeFilter !== 'all' ? 'No recipes match your filters.' : 'No recipes yet. Create your first recipe.'}</div>
                    </td>
                  </tr>
                ) : (
                  filtered.map(recipe => {
                    const tc = TYPE_COLORS[recipe.recipe_type] || TYPE_COLORS.other;
                    return (
                      <tr key={recipe.id}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 500, color: '#1e293b' }}>{recipe.name}</div>
                          {recipe.description && <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '2px' }}>{recipe.description.slice(0, 60)}{recipe.description.length > 60 ? '…' : ''}</div>}
                        </td>
                        <td style={{ padding: '12px 16px' }} className="d-none d-md-table-cell">
                          <Badge bg="" style={{ backgroundColor: tc.bg, color: tc.color, fontWeight: 500 }}>{recipe.recipe_type_display}</Badge>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                          {recipe.total_yield_g
                            ? <>{parseFloat(recipe.total_yield_g).toLocaleString()} g{(recipe as any).yield_quantity ? <><br /><span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600 }}>Makes {(recipe as any).yield_quantity} {(recipe as any).yield_unit}</span></> : ''}</>
                            : (recipe as any).yield_quantity ? <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600 }}>Makes {(recipe as any).yield_quantity} {(recipe as any).yield_unit}</span> : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-lg-table-cell">
                          {recipe.recipe_ingredients.length > 0
                            ? <Badge bg="" style={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 500 }}>{recipe.recipe_ingredients.length} items</Badge>
                            : '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div className="d-flex gap-1">
                            <Button variant="outline-success" size="sm" onClick={() => setViewingRecipe(recipe)} style={{ padding: '4px 8px' }} title="View recipe">
                              <EyeIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => handleOpenEdit(recipe)} style={{ padding: '4px 8px' }} title="Edit">
                              <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleConfirmDelete(recipe)} style={{ padding: '4px 8px' }} title="Delete">
                              <TrashIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </Container>

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={closeModal} size="xl" centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
              {editingRecipe ? `Edit Recipe — ${editingRecipe.name}` : 'Create New Recipe'}
            </Modal.Title>
            {!editingRecipe && <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Create a new recipe from scratch.</div>}
          </div>
        </Modal.Header>
        <Modal.Body>
          {feedback.error && <Alert variant="danger" className="mb-3">{feedback.error}</Alert>}

          {/* Basic fields */}
          <Row className="g-3 mb-4">
            <Col xs={12} md={5}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Recipe Name <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Enter recipe name" />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Recipe Type</Form.Label>
                <Form.Select size="sm" value={form.recipe_type} onChange={e => setForm(p => ({ ...p, recipe_type: e.target.value }))}>
                  {RECIPE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Total Yield (grams)</Form.Label>
                <Form.Control size="sm" type="number" min="0" value={form.total_yield_g} onChange={e => setForm(p => ({ ...p, total_yield_g: e.target.value }))} placeholder="850" />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Makes</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control size="sm" type="number" min="1" style={{ width: '90px', flexShrink: 0 }} value={(form as any).yield_quantity || ''} onChange={e => setForm(p => ({ ...p, yield_quantity: e.target.value } as any))} placeholder="1" />
                  <Form.Select size="sm" value={(form as any).yield_unit || 'loaf'} onChange={e => setForm(p => ({ ...p, yield_unit: e.target.value } as any))}>
                    {['loaf','loaves','roll','rolls','bun','buns','bagel','bagels','each','piece','pieces','dozen','tray','trays','batch','cookie','cookies','cake','croissant','croissants'].map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </Form.Select>
                </div>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Description</Form.Label>
                <Form.Control as="textarea" rows={2} size="sm" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Enter recipe description" />
              </Form.Group>
            </Col>
          </Row>

          {/* Recipe Ingredients */}
          <div className="p-3 mb-3 rounded" style={{ border: '1px solid #e2e8f0', borderRadius: '10px' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2 fw-bold" style={{ color: '#1e293b' }}>
                <BeakerIcon style={{ width: '18px', height: '18px', color: '#059669' }} />
                Recipe Ingredients
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={addIngredientLine} style={{ fontSize: '0.78rem', padding: '4px 10px' }}>
                  <BeakerIcon style={{ width: '13px', height: '13px', marginRight: '4px' }} />
                  Quick Add Ingredient
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={addStarterLine} style={{ fontSize: '0.78rem', padding: '4px 10px' }}>
                  <FireIcon style={{ width: '13px', height: '13px', marginRight: '4px' }} />
                  Quick Add Starter
                </Button>
              </div>
            </div>

            {lines.length === 0 ? (
              <div className="text-center py-3 text-muted" style={{ fontSize: '0.85rem' }}>
                No ingredients added yet. Click "Add Ingredient" to get started.
              </div>
            ) : (
              <div className="table-responsive mb-2">
                <Table size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', minWidth: '160px' }}>Ingredient / Starter</th>
                      <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', width: '90px' }}>Qty</th>
                      <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', width: '80px' }}>Unit</th>
                      <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>Notes</th>
                      <th style={{ width: '32px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, idx) => {
                      const isStarterLine = line.starter !== null || line.ingredient_unit === '__starter__';
                      return (
                        <tr key={idx}>
                          <td>
                            {isStarterLine ? (
                              <Form.Select size="sm" value={line.starter ?? ''} onChange={e => updateLine(idx, 'starter', e.target.value ? parseInt(e.target.value) : null)}>
                                <option value="">Select starter</option>
                                {allStarters.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </Form.Select>
                            ) : (
                              <Form.Select size="sm" value={line.ingredient ?? ''} onChange={e => updateLine(idx, 'ingredient', e.target.value ? parseInt(e.target.value) : null)}>
                                <option value="">Select ingredient</option>
                                {allIngredients.map((i: any) => <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>)}
                              </Form.Select>
                            )}
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" min="0" step="0.001" placeholder="0"
                              value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} />
                          </td>
                          <td>
                            <Form.Select size="sm" value={line.unit} onChange={e => updateLine(idx, 'unit', e.target.value)}
                              style={{ minWidth: '70px' }}>
                              <option value="">—</option>
                              {['g','kg','lbs','oz','l','ml','gallon','tsp','tbsp','cup','piece','box','bag'].map(u => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control size="sm" value={line.notes} onChange={e => updateLine(idx, 'notes', e.target.value)} placeholder="—" />
                          </td>
                          <td>
                            <Button variant="link" className="p-0 text-danger" onClick={() => removeLine(idx)} title="Remove">
                              <XMarkIcon style={{ width: '15px', height: '15px' }} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}

            <Button variant="outline-secondary" size="sm" onClick={addIngredientLine} className="w-100 mt-2" style={{ fontSize: '0.82rem' }}>
              <PlusIcon style={{ width: '13px', height: '13px', marginRight: '4px' }} />
              Add Ingredient
            </Button>
          </div>

          {/* Recipe Process */}
          <div className="p-3 rounded" style={{ border: '1px solid #e2e8f0', borderRadius: '10px' }}>
            <div className="d-flex align-items-center gap-2 fw-bold mb-2" style={{ color: '#1e293b' }}>
              <BookOpenIcon style={{ width: '18px', height: '18px', color: '#059669' }} />
              Recipe Process
            </div>
            <Form.Control as="textarea" rows={5} size="sm" value={form.process} onChange={e => setForm(p => ({ ...p, process: e.target.value }))}
              placeholder="Describe the recipe process... e.g., mixing times, order of ingredients, temperatures, fermentation periods, starter preparations, baking instructions" />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e2e8f0' }}>
          <Button variant="outline-secondary" size="sm" onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</> : (editingRecipe ? 'Save Changes' : 'Create Recipe')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Recipe Viewer */}
      <Modal show={!!viewingRecipe} onHide={() => setViewingRecipe(null)} size="xl" centered fullscreen="lg-down">
        {viewingRecipe && (() => {
          const tc = TYPE_COLORS[viewingRecipe.recipe_type] || TYPE_COLORS.other;
          const ingredients = viewingRecipe.recipe_ingredients || [];
          return (
            <>
              <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <div className="d-flex align-items-center gap-3">
                  <div style={{ backgroundColor: '#ecfdf5', borderRadius: '8px', padding: '8px', display: 'flex' }}>
                    <BookOpenIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
                  </div>
                  <div>
                    <Modal.Title style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b' }}>{viewingRecipe.name}</Modal.Title>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <Badge bg="" style={{ backgroundColor: tc.bg, color: tc.color, fontWeight: 600, fontSize: '0.78rem' }}>
                        {viewingRecipe.recipe_type_display || viewingRecipe.recipe_type}
                      </Badge>
                      {viewingRecipe.total_yield_g && (
                        <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                          {parseFloat(viewingRecipe.total_yield_g).toLocaleString()} g
                        </span>
                      )}
                      {(viewingRecipe as any).yield_quantity && (
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#059669', background: '#ecfdf5', borderRadius: '6px', padding: '2px 8px' }}>
                          Makes {(viewingRecipe as any).yield_quantity} {(viewingRecipe as any).yield_unit}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Modal.Header>
              <Modal.Body style={{ padding: '28px', background: '#fafafa' }}>
                <Row className="g-4">
                  {/* Left: Ingredients */}
                  <Col xs={12} md={5}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '10px' }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center gap-2 mb-4">
                          <BeakerIcon style={{ width: '18px', height: '18px', color: '#059669' }} />
                          <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                            Ingredients
                          </span>
                          <Badge bg="" style={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.72rem' }}>
                            {ingredients.length}
                          </Badge>
                        </div>
                        {ingredients.length === 0 ? (
                          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No ingredients added.</p>
                        ) : (
                          <div>
                            {ingredients.map((ri: any, i: number) => {
                              const isStarter = ri.starter !== null && ri.starter !== undefined;
                              return (
                                <div key={i} className="d-flex justify-content-between align-items-center py-3"
                                  style={{ borderBottom: i < ingredients.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                  <div className="d-flex align-items-center gap-2">
                                    {isStarter
                                      ? <FireIcon style={{ width: '14px', height: '14px', color: '#d97706', flexShrink: 0 }} />
                                      : <BeakerIcon style={{ width: '14px', height: '14px', color: '#64748b', flexShrink: 0 }} />
                                    }
                                    <span style={{ fontWeight: 500, fontSize: '0.9rem', color: '#1e293b' }}>
                                      {ri.display_name || ri.name_override || (ri.ingredient_name || ri.starter_name)}
                                    </span>
                                    {isStarter && (
                                      <Badge bg="" style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.65rem' }}>starter</Badge>
                                    )}
                                  </div>
                                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#059669', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                                    {ri.quantity} {ri.unit}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {viewingRecipe.description && (
                          <>
                            <hr style={{ borderColor: '#e2e8f0', margin: '20px 0' }} />
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Notes</div>
                            <p style={{ fontSize: '0.88rem', color: '#374151', margin: 0 }}>{viewingRecipe.description}</p>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Right: Process */}
                  <Col xs={12} md={7}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '10px' }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center gap-2 mb-4">
                          <BookOpenIcon style={{ width: '18px', height: '18px', color: '#059669' }} />
                          <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>Process</span>
                        </div>
                        {viewingRecipe.process ? (
                          <div style={{
                            fontSize: '0.95rem',
                            color: '#1e293b',
                            lineHeight: '1.8',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                          }}>
                            {viewingRecipe.process}
                          </div>
                        ) : (
                          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No process notes added yet.</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <Button variant="outline-secondary" size="sm" onClick={() => setViewingRecipe(null)}>Close</Button>
                <Button variant="success" size="sm" onClick={() => { setViewingRecipe(null); handleOpenEdit(viewingRecipe); }}>
                  <PencilSquareIcon style={{ width: '13px', height: '13px', marginRight: '4px' }} />
                  Edit Recipe
                </Button>
              </Modal.Footer>
            </>
          );
        })()}
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={deleteState.showModal} onHide={closeDeleteModal} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>Delete Recipe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Delete <strong>{deleteState.recipe?.name}</strong>? This cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={closeDeleteModal} disabled={deleteState.deleting}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleteState.deleting}>
            {deleteState.deleting ? <><Spinner size="sm" animation="border" className="me-1" />Deleting...</> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </CompanyLayout>
  );
};

export default CompanyRecipes;
